import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';

export interface PriceRange {
	min: number | null;
	max: number | null;
}

@Component({
	standalone: true,
	selector: 'app-price-filter',
	imports: [FormsModule, MatSliderModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	styles: `
		.slider-container {
			width: 100%;
			padding: 0 16px;
			box-sizing: border-box;
		}

		mat-slider {
			width: 100%;
			margin: 0;

			--mdc-slider-handle-color: var(--accent-color);
			--mdc-slider-focus-handle-color: var(--accent-color);
			--mdc-slider-hover-handle-color: var(--accent-color);
			--mdc-slider-active-track-color: var(--accent-color);

			--mat-mdc-slider-hover-ripple-color: transparent;
			--mat-mdc-slider-focus-ripple-color: transparent;
		}

		::ng-deep .mat-mdc-slider .mdc-slider__thumb::before,
		::ng-deep .mat-mdc-slider .mat-mdc-focus-indicator {
			display: none !important;
		}

		input[type='number']::-webkit-inner-spin-button,
		input[type='number']::-webkit-outer-spin-button {
			-webkit-appearance: none;
			margin: 0;
		}
		input[type='number'] {
			-moz-appearance: textfield;
		}
	`,
	template: `
		<div class="slider-container">
			<mat-slider [min]="minBound" [max]="maxBound" step="50">
				<input
					matSliderStartThumb
					[ngModel]="displayMin()"
					(ngModelChange)="displayMin.set($event)"
					(change)="emitChange()"
				/>
				<input
					matSliderEndThumb
					[ngModel]="displayMax()"
					(ngModelChange)="displayMax.set($event)"
					(change)="emitChange()"
				/>
			</mat-slider>

			<div class="flex items-center gap-4 mt-4">
				<div class="flex items-center gap-3 w-full">
					<label class="text-mainColor text-sm font-semibold whitespace-nowrap">Od</label>
					<input
						type="number"
						[ngModel]="displayMin()"
						(ngModelChange)="displayMin.set($event)"
						(focusout)="validateAndEmit()"
						(keydown.enter)="validateAndEmit()"
						class="h-12 w-full rounded-xl border border-mainColorLight px-4 text-mainColor font-semibold focus:outline-none bg-white md:h-14 md:bg-mainColorHoverBg md:border-mainColorHoverBg text-sm"
					/>
				</div>
				<span class="text-mainColor font-semibold">-</span>
				<div class="flex items-center gap-3 w-full">
					<label class="text-mainColor text-sm font-semibold whitespace-nowrap">Do</label>
					<input
						type="number"
						[ngModel]="displayMax()"
						(ngModelChange)="displayMax.set($event)"
						(focusout)="validateAndEmit()"
						(keydown.enter)="validateAndEmit()"
						class="h-12 w-full rounded-xl border border-mainColorLight px-4 text-mainColor font-semibold focus:outline-none bg-white md:h-14 md:bg-mainColorHoverBg md:border-mainColorHoverBg text-sm"
					/>
				</div>
			</div>
		</div>
	`,
})
export class PriceFilterComponent {
	min = input<number | null | undefined>(0);
	max = input<number | null | undefined>(15000);

	readonly minBound = 0;
	readonly maxBound = 15000;

	rangeChanged = output<PriceRange>();

	safeMin = computed(() => {
		const val = Number(this.min());
		return isNaN(val) || this.min() == null ? this.minBound : val;
	});

	safeMax = computed(() => {
		const val = Number(this.max());
		return isNaN(val) || this.max() == null ? this.maxBound : val;
	});

	displayMin = signal<number | null>(this.minBound);
	displayMax = signal<number | null>(this.maxBound);

	constructor() {
		effect(
			() => {
				this.displayMin.set(this.safeMin());
				this.displayMax.set(this.safeMax());
			},
			{ allowSignalWrites: true },
		);
	}

	emitChange(): void {
		const currentMin = this.displayMin();
		const currentMax = this.displayMax();

		this.rangeChanged.emit({
			min: currentMin === this.minBound ? null : currentMin,
			max: currentMax === this.maxBound ? null : currentMax,
		});
	}

	validateAndEmit(): void {
		const currentMin = this.displayMin();
		const currentMax = this.displayMax();

		const isMinEmpty = currentMin === null || currentMin === undefined || String(currentMin).trim() === '';
		const isMaxEmpty = currentMax === null || currentMax === undefined || String(currentMax).trim() === '';

		let newMin = isMinEmpty ? this.minBound : Number(currentMin);
		let newMax = isMaxEmpty ? this.maxBound : Number(currentMax);

		if (isNaN(newMin) || newMin < this.minBound) newMin = this.minBound;
		if (isNaN(newMax) || newMax > this.maxBound) newMax = this.maxBound;

		if (newMin > newMax) {
			newMin = newMax;
		}

		if (newMin !== currentMin || newMax !== currentMax) {
			this.displayMin.set(newMin);
			this.displayMax.set(newMax);
		}

		this.emitChange();
	}
}
